import React, { useState } from 'react';
import { RecoveryPoint, restoreRecoveryPoint } from '../api';
import { Callout, Dialog, Field, Input } from './primitives';
import { StatusBadge } from './ui';
import { FactList } from './project/ui';
import { labelFor, recoveryPointTypeLabel } from '../labels';
import { relativeTime } from './project/projectHealth';
import { useIdempotencyKey } from '../idempotency';

interface RestoreModalProps {
  recoveryPoint: RecoveryPoint;
  /** The environment being overwritten: named in the title and typed to confirm (UX-14). */
  environmentName: string;
  projectName?: string;
  currentUserEmail?: string;
  onClose: () => void;
  onRestoring: (restoreRunId: string, executionId: string) => void;
}

export const RestoreModal: React.FC<RestoreModalProps> = ({
  recoveryPoint,
  environmentName,
  projectName,
  currentUserEmail,
  onClose,
  onRestoring,
}) => {
  const [confirmationInput, setConfirmationInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  // The typed confirmation is the environment's name, like every other
  // destructive action on a named thing; the record keeps who confirmed.
  const confirmWord = environmentName;
  const isConfirmed = confirmationInput.trim().toLowerCase() === confirmWord.toLowerCase();
  const takenAt = new Date(recoveryPoint.completed_at ?? recoveryPoint.created_at);
  const takenWords = `${takenAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} (${relativeTime(takenAt.toISOString())})`;

  const handleRestore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmed) {
      setError(`Type "${confirmWord}" to confirm that ${confirmWord} is overwritten.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const resp = await restoreRecoveryPoint(
        recoveryPoint.id,
        {
          // Only the database can be restored for now (CO-169).
          mode: 'database_only',
          confirmedBy: currentUserEmail || confirmationInput.trim(),
        },
        idempotency.key(),
      );
      idempotency.settle();
      onRestoring(resp.restore_run.id, resp.execution.id);
    } catch (err: any) {
      idempotency.settle(err);
      setError(err.message || 'The server gave no reason.');
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open
      size="md"
      title={`Restore ${projectName ? `${projectName} · ` : ''}${environmentName} from a backup?`}
      onClose={onClose}
      busy={loading}
      onSubmit={handleRestore}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="button button--danger" disabled={!isConfirmed || loading}>
            {loading ? 'Starting restore' : 'Restore and replace the database'}
          </button>
        </>
      }
    >
      {error ? (
        <Callout tone="danger" title="The restore did not start">
          {error}
        </Callout>
      ) : null}

      <Callout tone="danger" title={`This replaces ${environmentName}’s live database.`}>
        Everything changed in {environmentName}’s database since {takenWords} (orders, customers, catalog and configuration)
        is lost unless you restore a newer backup. Media files are not touched. Commerce Ops takes a checkpoint of the
        current database first and rehearses the restore in a throwaway database before touching the live one.
      </Callout>
      {recoveryPoint.restore_test_status === 'restore_failed' ? (
        <Callout tone="warning" title="A restore test of this backup failed" testId="restore-test-failed">
          Restoring it may not produce a working store. Choose a backup whose restore test passed, or test this one again first.
        </Callout>
      ) : null}

      <FactList
        facts={[
          { label: 'Backup', value: `${labelFor(recoveryPointTypeLabel, recoveryPoint.type)}, ${takenWords}` },
          ...(recoveryPoint.commit_sha ? [{ label: 'Release', value: <code>{recoveryPoint.commit_sha.slice(0, 10)}</code> }] : []),
          { label: 'Restores', value: 'The database. Media files stay as they are.' },
          { label: 'Size', value: formatBytes(recoveryPoint.total_size_bytes) },
          { label: 'Integrity', value: <StatusBadge kind="verification" value={recoveryPoint.verification_status} /> },
          { label: 'Restore test', value: <StatusBadge kind="restoreTest" value={recoveryPoint.restore_test_status} /> },
        ]}
      />

      <Field label={<>Type <code>{confirmWord}</code> to confirm</>}>
        <Input
          type="text"
          value={confirmationInput}
          onChange={(e) => setConfirmationInput(e.target.value)}
          placeholder={confirmWord}
          autoFocus
          required
          autoComplete="off"
          spellCheck={false}
        />
      </Field>
    </Dialog>
  );
};
